import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import IntakeForm from "@/components/forms/IntakeForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HelpCircle } from "lucide-react";

export default function GetHelpPage() {
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-slate-100 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="md:flex md:items-center md:justify-between">
              <div className="md:max-w-2xl">
                <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl text-slate-900">We're Here to Help</h1>
                <p className="mt-4 text-xl text-slate-700">
                  Take the first step toward stability and meaningful employment by connecting with StaffAbility.
                </p>
              </div>
              <div className="mt-8 md:mt-0">
                <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 shadow-md">
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  <span>Free Services</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Eligibility</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Our programs are designed for individuals facing barriers to employment, including:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Justice-Involved Individuals</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>People with criminal records</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Those on probation or parole</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Recently released from incarceration</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Recovery & Mental Health</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>People in recovery from substance abuse</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Those managing mental health challenges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Individuals with dual diagnoses</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Other Barriers</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Experiencing homelessness or housing instability</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Long-term unemployment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Limited education or work history</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="text-slate-700">
                Not sure if you qualify? Contact us anyway! We're happy to discuss your situation and find the right resources for you.
              </p>
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">What to Expect</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Our process is designed to be supportive, comprehensive, and effective.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow-md overflow-hidden">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-4">
                        1
                      </div>
                      <h3 className="text-lg font-medium">Initial Assessment</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 pl-18">
                    <p className="text-slate-600 ml-12">
                      After you complete the intake form, we'll schedule a meeting (in person or virtual) to discuss your situation, goals, and needs. This helps us understand how to best support you.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-4">
                        2
                      </div>
                      <h3 className="text-lg font-medium">Case Worker Assignment</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-12">
                      You'll be matched with a dedicated case worker who will be your main point of contact and advocate throughout the program. They'll help coordinate all the services you need.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-4">
                        3
                      </div>
                      <h3 className="text-lg font-medium">Personalized Action Plan</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-12">
                      Together with your case worker, you'll develop a customized plan that outlines your goals, the services you'll access, and the steps needed to achieve stability and employment.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-4">
                        4
                      </div>
                      <h3 className="text-lg font-medium">Service Delivery</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-12">
                      Based on your needs, you'll participate in various programs such as job readiness training, housing support, education services, and more. Your case worker will help you navigate these services.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-4">
                        5
                      </div>
                      <h3 className="text-lg font-medium">Job Placement & Ongoing Support</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-12">
                      Once you're job-ready, we'll match you with employment opportunities and provide continued support after placement to ensure long-term success.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Answers to common questions about our programs and services.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow-md overflow-hidden">
                <AccordionItem value="faq-1">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <HelpCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                      <h3 className="text-lg font-medium">Is there a cost for your services?</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-8">
                      No, all of our services are provided at no cost to clients. We are funded through grants, donations, and partnerships with employers.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-2">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <HelpCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                      <h3 className="text-lg font-medium">How long does the program take?</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-8">
                      The length of time varies based on individual needs and goals. Some clients find employment within a few weeks, while others may be in our program for several months as they work through multiple barriers.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-3">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <HelpCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                      <h3 className="text-lg font-medium">What documents do I need to bring?</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-8">
                      If possible, please bring your ID, Social Security card, and any documentation related to your situation (court papers, treatment records, etc.). Don't worry if you don't have these items - we can still help you and will work on obtaining necessary documents.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-4">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <HelpCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                      <h3 className="text-lg font-medium">Can you guarantee I'll get a job?</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-8">
                      While we can't provide absolute guarantees, we have a strong success rate with our employer partners. Your success depends partly on your active participation and commitment to the program.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-5">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center text-left">
                      <HelpCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                      <h3 className="text-lg font-medium">What if I have specific needs or challenges?</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 ml-8">
                      Our program is personalized to meet your specific situation. We partner with many specialized service providers to address needs like substance abuse recovery, mental health support, housing, and more.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Intake Form */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50" id="intake-form">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Take the First Step</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Complete our intake form to get started with StaffAbility's services.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto text-center">
              <h3 className="text-xl font-bold mb-4 text-slate-900">Request Information</h3>
              <p className="mb-6 text-slate-600">
                Complete this form to receive more information about our programs and services.
              </p>
              <form className="text-left">
                <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                  <div>
                    <label htmlFor="first-name" className="block text-sm font-medium text-slate-700">First name</label>
                    <input
                      type="text"
                      id="first-name"
                      className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      disabled
                    />
                  </div>
                  <div>
                    <label htmlFor="last-name" className="block text-sm font-medium text-slate-700">Last name</label>
                    <input
                      type="text"
                      id="last-name"
                      className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      disabled
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700">Email</label>
                    <input
                      type="email"
                      id="email"
                      className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      disabled
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700">Phone number</label>
                    <input
                      type="tel"
                      id="phone"
                      className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      disabled
                    />
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-500 mb-4">This is a concept page. In a real implementation, this form would collect information from potential clients.</p>
                  <Link href="/get-involved">
                    <Button size="lg">Learn More About Our Concept</Button>
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
